import { Injectable } from '@angular/core';
import { AuthService } from '@security/service';
import { PermissionType } from '@shared/model';

@Injectable({
  providedIn: 'root'
})
export class PermissionHelperService {

  constructor(private authService: AuthService) {}

  /**
   * Vérifie si l'utilisateur a une permission spécifique pour une entité
   * @param entity - L'entité (ex: 'planning', 'employee', 'absence')
   * @param verb - Le type de permission (create, read, update, delete)
   * @returns true si l'utilisateur a la permission
   */
  hasPermission(entity: string, verb?: PermissionType): boolean {
    const user = this.authService.getCurrentUser();
    if (!user?.role?.permissions) {
      return false;
    }

    // 🚨 TEMPORAIRE: Simulation d'un utilisateur avec seulement permission READ
    // Décommentez ces lignes pour tester avec des permissions limitées
    /*
    if (entity === 'planning') {
      if (verb === 'read') return true;
      if (verb === 'create') return false;
      if (verb === 'update') return false;
      if (verb === 'delete') return false;
      if (verb === undefined) return true; // Au moins read
    }
    */

    return user.role.permissions.some(p => {
      if (!entity) {
        return true;
      }

      // Normalisation des entités selon la logique existante
      const normalizedEntity = this.normalizeEntity(entity);
      const normalizedPermissionEntity = this.normalizeEntity(p.entity);

      if (normalizedEntity !== normalizedPermissionEntity) {
        return false;
      }

      // Si aucun verbe spécifique, vérifier s'il a au moins une permission
      if (verb === undefined) {
        return p.perm_create || p.perm_read || p.perm_update || p.perm_delete;
      }

      // Vérifier la permission spécifique
      return p[`perm_${verb}`];
    });
  }

  /**
   * Normalise le nom d'une entité selon les règles métier
   */
  private normalizeEntity(entity: string): string {
    const normalizedEntity = entity.replace(/-/g, "").toLowerCase();

    // Mappings spéciaux selon la directive hasPermission existante
    const entityMappings: { [key: string]: string } = {
      'rank': 'employee',
      'employeesetting': 'employee',
      'absence': 'employee',
      'absencetype': 'employee',
      'generatortemplate': 'generator',
      'planningrestrictions': 'planning',
      'prestationgroup': 'prestationtype',
      'task': 'prestationtype',
      'exigencesperdays': 'exigences_per_days'
    };

    return entityMappings[normalizedEntity] || normalizedEntity;
  }

  /**
   * Vérifie les permissions multiples
   */
  hasAnyPermission(entities: string[], verb?: PermissionType): boolean {
    return entities.some(entity => this.hasPermission(entity, verb));
  }

  /**
   * Vérifie toutes les permissions
   */
  hasAllPermissions(entities: string[], verb?: PermissionType): boolean {
    return entities.every(entity => this.hasPermission(entity, verb));
  }

  /**
   * Retourne les permissions d'un utilisateur pour une entité spécifique
   */
  getEntityPermissions(entity: string): {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
  } {
    return {
      create: this.hasPermission(entity, 'create'),
      read: this.hasPermission(entity, 'read'),
      update: this.hasPermission(entity, 'update'),
      delete: this.hasPermission(entity, 'delete')
    };
  }

  /**
   * Vérifie si l'utilisateur est administrateur
   */
  isAdmin(): boolean {
    const user = this.authService.getCurrentUser();
    return user?.role?.admin ?? false;
  }

  /**
   * Vérifie si l'utilisateur a accès à l'éco-système (toujours autorisé)
   */
  hasEcoSystemAccess(): boolean {
    return true; // Selon la directive existante
  }

  /**
   * Vérifie les entités spéciales qui sont toujours autorisées
   */
  isAlwaysAllowedEntity(entity: string): boolean {
    const alwaysAllowed = ['eco-system', 'absence', 'exchange-request'];
    return alwaysAllowed.includes(entity);
  }
}
